import type { ReactNode } from 'react';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { ReactionType } from '~/model/ReactionType';
import { VpButton, VpIcon, VpSticker } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import htmlReactParser from 'html-react-parser';
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { LinkItUrl } from 'react-linkify-it';
import { Element } from 'react-scroll';
import { fetchItem } from '@/app/actions/server';
import { Translate } from '@/app/components/Translate/Translate';
import { getLangAttributeValue } from '@/app/utils/entities/translation';
import { Avatar } from '~/components/commons/Avatar/Avatar';
import { Zoom } from '~/components/commons/Zoom/Zoom';
import appConst from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';
import { isMainUser } from '~/utils/user/user';
import { convertLinebreaks } from '~/utils/utils';
import { useAuthenticatedContext } from '../Provider';
import { ReportCommentAbuse } from './ReportCommentAbuse/ReportCommentAbuse';
import { getReactionIcon } from './utils';

export const SubItem = ({
  subitem,
  item,
  postReaction,
  deleteReaction,
  processing,
  isItemPublished,
  locked = false,
  opener,
}: {
  subitem: AnswerType & CommentType;
  item: AnswerType & CommentType;
  processing: boolean;
  isItemPublished: boolean;
  locked?: boolean;
  opener?: ReactNode;
  postReaction: (
    itemId: number,
    subItemId: number,
    reaction: string,
  ) => Promise<ReactionType>;
  deleteReaction: (
    itemId: number,
    subItemId: number,
    reaction: ReactionType,
  ) => Promise<void>;
}) => {
  const locale = useLocale();
  const searchParams = useSearchParams();
  const format = useFormatter();
  const t = useTranslations('DISCUSSION');

  const {
    current_user_reaction,
    id: commentId,
    media,
    parent,
    reactions_repartition,
    value_text,
    value,
    original_locale: originalLocale,
    '@type': type,
    contribution,
    created_by,
  } = subitem;

  const urlTargetted = parent
    ? searchParams.get('sc') === `${subitem.id}`
    : searchParams.get('c') === `${subitem.id}` && !searchParams.get('sc');

  // Get comment text depending on the type of comment (Answer||Comment)
  const { id: itemId } = item;

  const [text, setText] = useState(value_text || value);
  const [loading, setLoading] = useState(false);
  const [subItemTranslated, setSubItemTranslated] = useState(false);

  const reactionValue = current_user_reaction?.value;

  const reactionIcon = getReactionIcon(
    reactionValue,
    current_user_reaction !== null,
  );

  const { user } = useAuthenticatedContext();

  const handleReaction = () => {
    setLoading(true);
    if (current_user_reaction) {
      deleteReaction(
        itemId,
        parent ? commentId : null,
        current_user_reaction,
      ).then(() => setLoading(false));
    } else {
      postReaction(itemId, parent ? commentId : null, 'like').then(() =>
        setLoading(false),
      );
    }
  };

  const getTranslation = async () => {
    setLoading(true);
    const response = await fetchItem<AnswerType & CommentType>({
      pathname: `${type === appConst.entityTypes.comment ? 'comments' : 'answers'}/${commentId}`,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
    });

    setText(response.value);
    setSubItemTranslated(subItemTranslated);
    setLoading(false);
  };

  const userInfo = contribution?.created_for || created_by;
  const hasNoLike = reactions_repartition?.like < 1;

  const reactionLabel = hasNoLike
    ? t('LIKE_ACTION')
    : t('LIKE', { count: reactions_repartition?.like });

  const linkifiedMarkup = renderToStaticMarkup(
    <LinkItUrl>
      {text}
    </LinkItUrl>,
  ).toString();

  const withLineBreaks = convertLinebreaks(linkifiedMarkup);
  const itemContent = htmlReactParser(withLineBreaks);

  const langValue = getLangAttributeValue(originalLocale, AcceptLanguage[locale]);

  const shouldAddLangAttribute
    = subItemTranslated && originalLocale !== AcceptLanguage[locale];

  return isItemPublished
    ? (
        <>
          <div
            className={classNames('thread-idea__author', {
              'thread-idea__author--admin': subitem.as_admin,
            })}
          >
            <Avatar user={userInfo} avatarSize={32} />
          </div>
          <div
            data-testid="content"
            className={classNames('thread-idea__content', {
              'thread-idea__content--highlighted': urlTargetted,
            })}
          >
            {(text || media) && (
              <div className="thread-idea__identity">
                <div className="vp-subtitle-m" data-testid="firstname">
                  {userInfo.firstname}
                </div>
                {subitem.as_admin && (
                  <VpSticker
                    variant="secondary"
                    size="small"
                    data-testid="user-label"
                  >
                    {userInfo.label ?? userInfo.job}
                  </VpSticker>
                )}
                <span className="thread-idea__date">
                  {format.relativeTime(new Date(subitem.created_at))}
                </span>
              </div>
            )}
            <Element name={`c-${subitem.id}`}>
              <div className="thread-idea__content-body">
                {text && (
                  <span
                    lang={(shouldAddLangAttribute && langValue) || null}
                    className="thread-idea__text"
                    data-cy="ideaText"
                  >
                    {itemContent}
                  </span>
                )}
                {media && (
                  <span
                    className="thread-idea__picture"
                    data-testid="comment-picture"
                  >
                    <div className="thread-idea__picture-wrapper">
                      <Image
                        alt=""
                        data-cy="comment-img"
                        data-testid="comment-img"
                        fill
                        style={{ objectFit: 'cover' }}
                        src={media.content_url}
                      />
                      <Zoom mainSrc={media.content_url} />
                    </div>
                  </span>
                )}
              </div>
              {text && (
                <Translate
                  getTranslation={getTranslation}
                  originalLanguage={originalLocale}
                  translated={subItemTranslated}
                  entityUri={subitem['@id']}
                  userLanguage={AcceptLanguage[locale]}
                  fromComment
                />
              )}
            </Element>
          </div>
          <div className="thread-idea__report">
            <ReportCommentAbuse subitem={subitem} />
          </div>
          <div className="thread-idea__actions">
            {isMainUser(user) && (
              <div className="thread-idea__reactions">
                <VpButton
                  data-testid="like"
                  data-cy="like"
                  onClick={handleReaction}
                  disabled={loading || processing || locked}
                  variant="tertiary"
                  size="small"
                  className={classNames('thread-idea__reaction-button', {
                    'thread-idea__reaction-button--active': !!reactionValue,
                  })}
                  startSlot={<VpIcon name={reactionIcon} />}
                >
                  <span>{reactionLabel}</span>
                </VpButton>
              </div>
            )}
            {opener}
          </div>
        </>
      )
    : (
        <>
          <div className="thread-idea__author" />
          <div
            className={classNames('thread-idea__content', {
              'thread-idea__content--highlighted': urlTargetted,
            })}
          >
            <div className="thread-idea__content-body">
              <span className="thread-idea__text">
                {t('MODERATED_CONTENT')}
              </span>
            </div>
          </div>
          <div className="thread-idea__reactions" />
        </>
      );
};
